import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import moment from 'moment';
import Divider from '@material-ui/core/Divider';
import PayrollComparisonTable from '../report/summery/PayrollComparisonTable';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';

// Props:
// open: boolean
// onClose: function
// onSubmit: function(remarkText)
// user: string (current user)
// showInput: boolean (if false, just display the remarks)
// remarks: [{ user, date, remark }] (array of remarks)
// payrollRun: current payroll run
// payrollRun1: previous payroll run to compare against

const ReviewRemarkDialog = ({ open, onClose, onSubmit, user, showInput, remarks = [], payrollRun, payrollRun1 }) => {
  const [remarkText, setRemarkText] = useState('');
  const [reviewData, setReviewData] = useState({ checkedEmployeeIds: [], payrollNotes: {} });
  const [nextUsers, setNextUsers] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    async function fetchUsers() {
      try {
        const domain = localStorage.getItem('domain');
        const response = await fetch(domain + '/api/users/companyUsers', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            'X-COMPANY': localStorage.getItem('company') || 'ROOT',
            'Bypass-Tunnel-Reminder': 'true'
          }
        });
        if (response.ok) {
          const data = await response.json();
          setUsers(data.filter(u => (u.email !== user && u.username !== user) && u.isAvailable && (u.roles.includes("Reviewer") || u.roles.includes("FinalReviewer"))));
        } else {
          setUsers([]);
        }
      } catch (e) {
        setUsers([]);
      }
    }
    fetchUsers();
  }, [user]);

  useEffect(() => {
      if (payrollRun && payrollRun.remarks && showInput) {
          const initialCheckedEmployeeIds = [];
          const initialNotes = {};
          // Build payrollId -> employeeId map
          var payrollIdToEmp = {};
          (payrollRun.payrolls || []).forEach(function(p) { payrollIdToEmp[p.id] = p.employeeId; });
          payrollRun.remarks.forEach(remark => {
              if (remark.checkedPayrollIds) {
                  // Convert payrollIds to employeeIds
                  remark.checkedPayrollIds.forEach(function(pid) {
                      var eid = payrollIdToEmp[pid];
                      if (eid) initialCheckedEmployeeIds.push(eid);
                  });
              }
              if (remark.checkedEmployeeIds) {
                  initialCheckedEmployeeIds.push(...remark.checkedEmployeeIds);
              }
              if (remark.payrollNotes && Array.isArray(remark.payrollNotes)) {
                  remark.payrollNotes.forEach(item => {
                      // Convert payrollId to employeeId
                      var eid = item.employeeId || payrollIdToEmp[item.payrollId];
                      if (eid) {
                          initialNotes[eid] = item.note;
                      }
                  });
              }
          });
          setReviewData({
              checkedEmployeeIds: [...new Set(initialCheckedEmployeeIds)],
              payrollNotes: initialNotes
          });
      }
  }, [payrollRun, showInput]);

  const handleSubmit = () => {
    if (remarkText.trim() || reviewData.checkedEmployeeIds.length > 0 || Object.keys(reviewData.payrollNotes).length > 0) {
      // Save notes as array of { employeeId, note }
      const payrollNotesAsArray = Object.keys(reviewData.payrollNotes).map(eid => ({
          employeeId: eid,
          note: reviewData.payrollNotes[eid]
      }));
      onSubmit({
        type: 'review',
        user: user,
        date: new Date(),
        remarkText: remarkText.trim(),
        checkedEmployeeIds: reviewData.checkedEmployeeIds,
        payrollNotes: payrollNotesAsArray,
        nextUsers: nextUsers
      });
      setRemarkText('');
      setReviewData({ checkedEmployeeIds: [], payrollNotes: {} });
      setNextUsers([]);
    }
  };
  
  const handleComparisonChange = (data) => {
    setReviewData(data);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xlg" fullWidth>
      <DialogTitle>{showInput ? 'Mark Payroll as Reviewed' : 'Review Remarks'}</DialogTitle>
      <DialogContent>
        {showInput && payrollRun && payrollRun1 && (
          <PayrollComparisonTable 
            payrollRun={payrollRun} 
            payrollRun1={payrollRun1}  
            initialCheckedIds={reviewData.checkedEmployeeIds}
            initialNotes={reviewData.payrollNotes}
            onReviewDataChange={handleComparisonChange}
          />
        )}
        {showInput ? (
          <>
            <Typography gutterBottom>
              Please enter your review remark for this payroll run.
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              label="Remark"
              type="text"
              fullWidth
              multiline
              rows={3}
              value={remarkText}
              onChange={e => setRemarkText(e.target.value)}
            />
            <div style={{ marginTop: 16, marginBottom: 8 }}>
              <Typography gutterBottom>Select Next Reviewer(s)</Typography>
              <Select
                multiple
                value={nextUsers}
                onChange={e => setNextUsers(e.target.value)}
                displayEmpty
                fullWidth
                renderValue={selected =>
                  selected && selected.length > 0
                    ? selected.join(', ')
                    : <em>None</em>
                }
              >
                <MenuItem value="" disabled>
                  <em>None</em>
                </MenuItem>
                {users && users.map(u => (
                  <MenuItem key={u.id || u.email} value={u.email}>
                    {u.email}
                  </MenuItem>
                ))}
              </Select>
            </div>
            {remarks.length > 0 && (
              <>
                <Typography variant="subtitle1" style={{ marginTop: 16, marginBottom: 8 }}>
                  Previous Remarks:
                </Typography>
                {remarks.map((remark, index) => (
                  <React.Fragment key={index}>
                    <Grid container direction="column" spacing={1}>
                      <Grid item>
                        <Typography><strong>Reviewer:</strong> {remark.user}</Typography>
                      </Grid>
                      <Grid item>
                        <Typography><strong>Date:</strong> {moment(remark.date).format('YYYY-MM-DD HH:mm')}</Typography>
                      </Grid>
                      <Grid item>
                        <Typography><strong>Remark:</strong> {remark.remarkText}</Typography>
                      </Grid>
                    </Grid>
                    {index < remarks.length - 1 && <Divider style={{ margin: '8px 0' }} />}
                  </React.Fragment>
                ))}
              </>
            )}
          </>
        ) : (
          <Grid container direction="column" spacing={2}>
            {remarks.length > 0 ? (
              remarks.map((remark, index) => (
                <React.Fragment key={index}>
                  <Grid item>
                    <Grid container direction="column" spacing={1}>
                      <Grid item>
                        <Typography><strong>Reviewer:</strong> {remark.user}</Typography>
                      </Grid>
                      <Grid item>
                        <Typography><strong>Date:</strong> {moment(remark.date).format('YYYY-MM-DD HH:mm')}</Typography>
                      </Grid>
                      <Grid item>
                        <Typography><strong>Remark:</strong> {remark.remarkText}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  {index < remarks.length - 1 && <Divider style={{ margin: '8px 0' }} />}
                </React.Fragment>
              ))
            ) : (
              <Typography>No remarks available.</Typography>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {showInput ? 'Cancel' : 'Close'}
        </Button>
        {showInput && (
          <Button onClick={handleSubmit} color="primary" variant="contained" disabled={!remarkText.trim() && reviewData.checkedEmployeeIds.length === 0 && Object.keys(reviewData.payrollNotes).length === 0}>
            Submit
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReviewRemarkDialog; 